import { createSlice, current, isAnyOf } from "@reduxjs/toolkit";
import type { ConditionField, MeasureField } from "../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import { removeDataColumn as removeStatementDataColumn } from "../../components/builder/financialStatement/utils/statementColumns.ts";
import { remapBrokenDownPeriodGuids } from "../../components/builder/financialStatement/utils/statementColumnPeriod.ts";
import {
  indentLine as indentStatementLine,
  insertLine,
  insertLines,
  moveLine as moveStatementLine,
  moveLineTo as moveStatementLineTo,
  normalizeSpacerLevels,
  outdentLine as outdentStatementLine,
  removeLine as removeStatementLine,
} from "../../components/builder/financialStatement/utils/statementLines.ts";
import {
  createFinancialStatementReportConfiguration,
  createStatementDataColumn,
} from "../../components/builder/utils/configuration/financialStatementConfiguration.ts";
import { getDimensionNames } from "../../utilities/getDimensionNames.ts";
import { getMeasureNames } from "../../utilities/getMeasureNames.ts";
import { createFinancialStatementDefaultSettings } from "../../utilities/isConfigurationValid.ts";
import { insertItemAt } from "../../utilities/Utilities.tsx";
import type {
  FinancialStatementFieldsActionTypes,
  FinancialStatementFieldState,
  StatementLineState,
} from "../types/fieldsState.types.ts";
import {
  calculateAndAssignSystemLabel,
  moveConditionItem,
  removeConditionFromLinked,
  removeConditionItem,
  updateConditionConfig,
  updateConditionItem,
} from "../utils/fieldsState.ts";
import { updateLinkAndValidate } from "../utils/fieldsUtils.ts";
import { FINANCIAL_STATEMENT_SLICE_NAME } from "../utils/storeUtils.ts";

const createInitialState = (): FinancialStatementFieldState => ({
  conditions: [],
  lines: [],
  axes: [],
  dataColumns: [createStatementDataColumn()],
  settings: createFinancialStatementDefaultSettings(),
  dimensionNames: [],
  measureNames: [],
  configuration: undefined,
});

const getLineFields = (lines: StatementLineState[]): MeasureField[] =>
  lines.flatMap((line) => (line.field ? [line.field] : []));

const financialStatementFieldsSlice = createSlice({
  name: FINANCIAL_STATEMENT_SLICE_NAME,
  initialState: createInitialState,
  reducers: {
    resetToDefaultAction: () => createInitialState(),
    setConditions: (state, action: FinancialStatementFieldsActionTypes["setConditions"]) => {
      state.conditions = action.payload;
    },
    addCondition: (state, action: FinancialStatementFieldsActionTypes["addCondition"]) => {
      const condition: ConditionField = { ...action.payload.field };
      calculateAndAssignSystemLabel(condition, state.conditions);
      state.conditions = insertItemAt(state.conditions, condition, action.payload.index);
    },
    removeCondition: (state, action: FinancialStatementFieldsActionTypes["removeCondition"]) => {
      const guid = action.payload.config.guid;
      state.conditions = removeConditionItem(action.payload, state.conditions);
      removeConditionFromLinked(guid, getLineFields(state.lines));
      state.dataColumns.forEach((column) => {
        if (column.linkedConditions.includes(guid)) {
          column.linkedConditions = column.linkedConditions.filter((linkedGuid) => linkedGuid !== guid);
          column.dateOffsetMonths = undefined;
        }
      });
    },
    moveCondition: (state, action: FinancialStatementFieldsActionTypes["moveCondition"]) => {
      state.conditions = moveConditionItem(action.payload.field, action.payload.newIndex, state.conditions);
    },
    updateCondition: (state, action: FinancialStatementFieldsActionTypes["updateCondition"]) => {
      state.conditions = updateConditionItem(action.payload.field, action.payload.changes, state.conditions);
    },
    updateConditionConfig: (state, action: FinancialStatementFieldsActionTypes["updateConditionConfig"]) => {
      const condition = state.conditions.find((c) => c.config.guid === action.payload.field.config.guid);
      if (condition) {
        condition.config = updateConditionConfig(condition.config, action.payload.changes);
      }
    },
    setLines: (state, action: FinancialStatementFieldsActionTypes["setLines"]) => {
      state.lines = action.payload;
    },
    addLine: (state, action: FinancialStatementFieldsActionTypes["addLine"]) => {
      state.lines = insertLine(state.lines, action.payload.line, action.payload.anchor);
      state.selectedLineId = action.payload.line.guid;
    },
    addLines: (state, action: FinancialStatementFieldsActionTypes["addLines"]) => {
      state.lines = insertLines(state.lines, action.payload.lines, action.payload.anchor);
      state.selectedLineId = action.payload.lines[0]?.guid;
    },
    updateLine: (state, action: FinancialStatementFieldsActionTypes["updateLine"]) => {
      const line = state.lines.find((l) => l.guid === action.payload.guid);
      if (line) {
        Object.assign(line, action.payload.changes);
      }
    },
    updateLineField: (state, action: FinancialStatementFieldsActionTypes["updateLineField"]) => {
      const field = state.lines.find((l) => l.guid === action.payload.guid)?.field;
      if (field) {
        field.config = { ...field.config, ...action.payload.changes };
      }
    },
    removeLine: (state, action: FinancialStatementFieldsActionTypes["removeLine"]) => {
      state.lines = removeStatementLine(state.lines, action.payload);
      state.dataColumns.forEach((column) => {
        if (column.periodLineGuids?.includes(action.payload)) {
          column.periodLineGuids = column.periodLineGuids.filter((guid) => guid !== action.payload);
        }
      });
    },
    indentLine: (state, action: FinancialStatementFieldsActionTypes["indentLine"]) => {
      state.lines = indentStatementLine(state.lines, action.payload);
    },
    outdentLine: (state, action: FinancialStatementFieldsActionTypes["outdentLine"]) => {
      state.lines = outdentStatementLine(state.lines, action.payload);
    },
    moveLine: (state, action: FinancialStatementFieldsActionTypes["moveLine"]) => {
      state.lines = moveStatementLine(state.lines, action.payload.guid, action.payload.direction);
    },
    moveLineTo: (state, action: FinancialStatementFieldsActionTypes["moveLineTo"]) => {
      state.lines = moveStatementLineTo(state.lines, action.payload.guid, action.payload.target);
      state.selectedLineId = action.payload.guid;
    },
    applyLineBreakDown: (state, action: FinancialStatementFieldsActionTypes["applyLineBreakDown"]) => {
      const previousLines = current(state.lines);
      state.lines = action.payload.lines;
      state.selectedLineId = action.payload.selectedGuid;
      state.dataColumns.forEach((column) => {
        if (column.periodLineGuids?.length) {
          column.periodLineGuids = remapBrokenDownPeriodGuids(previousLines, state.lines, column.periodLineGuids);
        }
      });
    },
    setSelectedLineId: (state, action: FinancialStatementFieldsActionTypes["setSelectedLineId"]) => {
      state.selectedLineId = action.payload;
    },
    setAxes: (state, action: FinancialStatementFieldsActionTypes["setAxes"]) => {
      state.axes = action.payload;
    },
    addAxis: (state, action: FinancialStatementFieldsActionTypes["addAxis"]) => {
      state.axes = [...state.axes, action.payload];
    },
    updateAxis: (state, action: FinancialStatementFieldsActionTypes["updateAxis"]) => {
      const axis = state.axes.find((a) => a.field.config.guid === action.payload.guid);
      if (axis) {
        Object.assign(axis, action.payload.changes);
      }
    },
    removeAxis: (state, action: FinancialStatementFieldsActionTypes["removeAxis"]) => {
      state.axes = state.axes.filter((axis) => axis.field.config.guid !== action.payload);
    },
    makeOuterAxis: (state, action: FinancialStatementFieldsActionTypes["makeOuterAxis"]) => {
      const axis = state.axes.find((a) => a.field.config.guid === action.payload);
      if (axis) {
        state.axes = [axis, ...state.axes.filter((a) => a.field.config.guid !== action.payload)];
      }
    },
    setDataColumns: (state, action: FinancialStatementFieldsActionTypes["setDataColumns"]) => {
      state.dataColumns = action.payload;
    },
    addDataColumn: (state, action: FinancialStatementFieldsActionTypes["addDataColumn"]) => {
      state.dataColumns = [...state.dataColumns, action.payload];
    },
    updateDataColumn: (state, action: FinancialStatementFieldsActionTypes["updateDataColumn"]) => {
      const column = state.dataColumns.find((c) => c.guid === action.payload.guid);
      if (column) {
        Object.assign(column, action.payload.changes);
      }
    },
    removeDataColumn: (state, action: FinancialStatementFieldsActionTypes["removeDataColumn"]) => {
      state.dataColumns = removeStatementDataColumn(state.dataColumns, action.payload);
    },
    updateSettings: (state, action: FinancialStatementFieldsActionTypes["updateSettings"]) => {
      Object.assign(state.settings, action.payload);
    },
    setSettings: (state, action: FinancialStatementFieldsActionTypes["setSettings"]) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    const configurationActions = Object.values(financialStatementFieldsSlice.actions).filter(
      (action) => action !== financialStatementFieldsSlice.actions.setSelectedLineId
    );
    builder.addMatcher(isAnyOf(...configurationActions), (state) => {
      state.lines = normalizeSpacerLevels(state.lines);
      if (!state.lines.some((line) => line.guid === state.selectedLineId)) {
        state.selectedLineId = undefined;
      }
      state.conditions = updateLinkAndValidate(
        state.conditions,
        getLineFields(state.lines),
        ReportType.FinancialStatement
      );
      const configuration = createFinancialStatementReportConfiguration(state);
      state.configuration = configuration;
      state.isConfigurationEmpty =
        configuration.conditions.length === 0 &&
        configuration.lines.length === 0 &&
        configuration.columns.axes.length === 0 &&
        configuration.columns.dataColumns.length === 1;
      state.dimensionNames = getDimensionNames(ReportType.FinancialStatement, configuration);
      state.measureNames = getMeasureNames(ReportType.FinancialStatement, configuration);
    });
  },
});

export const financialStatementFieldsActions = financialStatementFieldsSlice.actions;
export const financialStatementFieldsReducer = financialStatementFieldsSlice.reducer;
